import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface TruncatedTextProps {
  children: React.ReactNode;
  maxLines?: number;
  className?: string;
  showButton?: boolean;
}

/**
 * TruncatedText - Responsive text truncation component
 * 
 * Truncates text to specified number of lines on mobile/tablet only.
 * Desktop always shows full text.
 * 
 * @param children - Text content to potentially truncate
 * @param maxLines - Max lines before truncation (default: 3)
 * @param className - Additional CSS classes
 * @param showButton - Show expand/collapse button (default: true)
 */
export const TruncatedText = ({ 
  children, 
  maxLines = 3, 
  className = '',
  showButton = true 
}: TruncatedTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Desktop always shows full text
  if (!isMobile) {
    return <div className={className}>{children}</div>;
  }

  // Mobile/tablet with truncation
  return (
    <div>
      <div 
        className={`${className} ${!isExpanded ? 'line-clamp-' + maxLines : ''}`}
        style={!isExpanded ? { 
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        } : undefined}
      >
        {children}
      </div>
      {showButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-xs h-auto py-1 px-2"
        >
          {isExpanded ? (
            <>
              Show Less <ChevronUp className="w-3 h-3 ml-1" />
            </>
          ) : (
            <>
              Read More <ChevronDown className="w-3 h-3 ml-1" />
            </>
          )}
        </Button>
      )}
    </div>
  );
};